import { Link } from 'react-router-dom';
import { ChevronRight, MoreHorizontal, Plus, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PermissionGuard } from '@/components/PermissionGuard';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PERMISSIONS } from '@features/shared/constants/permissions';

/**
 * PW-13 — the two pieces of the detail page's fixed header that are worth their own file.
 */

/**
 * `← Picklists › PL-0143`.
 *
 * Replaces a bare ghost `ArrowLeft` icon button. The destination was always right — it carries
 * `?from=closed` so Back returns to the tab you came from — but an unlabelled arrow says nothing about
 * where it goes, and this page is reachable from two different lists.
 */
export function PicklistBreadcrumb({
  backUrl,
  picklistNumber,
  picklistUrl,
  current,
}: {
  backUrl: string;
  picklistNumber?: string;
  /**
   * Makes the picklist crumb a link. Only pass it when the picklist is NOT the current page —
   * on the detail page itself the number is where you already are, so it stays plain text.
   */
  picklistUrl?: string;
  /**
   * A trailing crumb for pages that sit UNDER a picklist or under the list ("Create Picklist",
   * "Add items"). Not `font-mono` — these are words, not a document number.
   */
  current?: string;
}) {
  const picklistCrumb = picklistUrl ? (
    <Link
      to={picklistUrl}
      className="rounded-sm font-mono hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {picklistNumber}
    </Link>
  ) : (
    <span className="font-mono text-foreground">{picklistNumber}</span>
  );

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm text-muted-foreground">
      <Link
        to={backUrl}
        className="rounded-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        ← Picklists
      </Link>
      {picklistNumber && (
        <>
          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
          {picklistCrumb}
        </>
      )}
      {current && (
        <>
          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
          <span className="font-medium text-foreground">{current}</span>
        </>
      )}
    </nav>
  );
}

interface OverflowMenuProps {
  canEdit: boolean;
  canCancel: boolean;
  onAddItems: () => void;
  onCancel: () => void;
}

/**
 * Add Items · Cancel, behind one `ellipsis` trigger.
 *
 * ## Why these two are in a menu and not on the row
 *
 * Six controls at 36px plus a wrapping title do not fit beside the activity rail at 1280px. The design
 * drew four; the page ships more, and D10's verdict was to KEEP the extra ones rather than delete them:
 * without Cancel the UI would render a "Cancelled" pill for a state it can no longer reach, and without
 * Add Items a short-picked picklist cannot be amended.
 *
 * ## Print is gone, and Close moved out to the action row (owner, 2026-09-14)
 *
 * D10 had left Print here pending a warehouse confirmation that nobody depended on the raw
 * `window.print()` output. The owner made that call directly: **delete it**. Close picklist went the
 * other way — it is the only terminal state a part-picked picklist can reach (auto-close fires only when
 * every line is fully picked *and* posted), so it is now a labelled button on the header row instead of
 * an item two clicks deep. Its `canClose` guard and permission guard travelled with it unchanged.
 *
 * ## Two rules on the menu itself
 *
 * Each item keeps its own `PermissionGuard`, which renders NOTHING when the permission is missing — so a
 * user sees no item rather than a disabled one. And the trigger does not render when every item is guarded
 * away, because an ellipsis that opens an empty menu is a new dead end.
 */
export function PicklistHeaderActions({
  canEdit,
  canCancel,
  onAddItems,
  onCancel,
}: OverflowMenuProps) {
  // Both guards render NOTHING when the permission is missing, so a user sees no control rather than a
  // disabled one — and with neither, the row has no actions and no trigger at all.
  if (!canEdit && !canCancel) return null;

  const addItems = canEdit && (
    <PermissionGuard permission={PERMISSIONS.ISSUANCE_EDIT_PICKLIST}>
      <Button variant="outline" size="sm" onClick={onAddItems}>
        <Plus className="mr-1.5 h-4 w-4" />
        Add items
      </Button>
    </PermissionGuard>
  );

  const cancel = canCancel && (
    <PermissionGuard permission={PERMISSIONS.ISSUANCE_CANCEL_PICKLIST}>
      {/* Text variant, not outline: cancelling is terminal and rarely what someone came here to do, so
          it reads as the quietest thing on the row while still being one click away. The destructive
          colour is what marks it, not a border competing with Add items. */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onCancel}
        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
      >
        <Ban className="mr-1.5 h-4 w-4" />
        Cancel picklist
      </Button>
    </PermissionGuard>
  );

  return (
    <>
      {/* Both forms are rendered and the CONTAINER decides which is shown — see `.pl-cq-actions-*` in
          globals.css. A container query, not a media query: the rail and module panel PUSH `main`, so
          this column narrows while the window does not move, and a viewport breakpoint would keep two
          labelled buttons on a column with no room for them (UI-GUIDELINES §6).

          Rendered rather than branched in JS because measuring width in render is state React cannot
          track, and the menu's contents are three elements — cheaper to hide than to re-render on a
          resize observer. */}
      <div className="pl-cq-actions-inline">
        {addItems}
        {cancel}
      </div>

      <div className="pl-cq-actions-menu">
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                {/* Icon-only, so it carries both a tooltip and an accessible name — UI-GUIDELINES §5. */}
                <Button variant="outline" size="sm" aria-label="More picklist actions">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>More actions</TooltipContent>
          </Tooltip>
          <DropdownMenuContent align="end">
            {canEdit && (
              <PermissionGuard permission={PERMISSIONS.ISSUANCE_EDIT_PICKLIST}>
                <DropdownMenuItem onSelect={onAddItems}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add items
                </DropdownMenuItem>
              </PermissionGuard>
            )}
            {canCancel && (
              <PermissionGuard permission={PERMISSIONS.ISSUANCE_CANCEL_PICKLIST}>
                <DropdownMenuItem onSelect={onCancel} className="text-destructive focus:text-destructive">
                  <Ban className="mr-2 h-4 w-4" />
                  Cancel picklist
                </DropdownMenuItem>
              </PermissionGuard>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}
